// ============================================================
// Services Page (Services 2) — top-level catalog
// Taxonomy mirrors interactmarketing.com (Search / Social /
// Content / Websites / Specialty Media) with Slingshot's
// modern SEO + GEO + PPC positioning.
// ============================================================
import {
  Search,
  Sparkles,
  MousePointerClick,
  Repeat,
  MapPin,
  ShieldCheck,
  Megaphone,
  Users,
  Video,
  FileText,
  Link2,
  MonitorSmartphone,
  PenTool,
  Radar,
  Tv,
  Cog,
  Workflow,
  BarChart3,
} from "lucide-react";

export const SERVICES_PAGE = {
  hero: {
    eyebrow: "Full-Service Digital Marketing \u00b7 Since 2006",
    titlePre: "Every Channel That Matters. ",
    titleAccent: "One Machine.",
    sub: "SEO, GEO, PPC, content, social and specialty media \u2014 engineered as one connected system, so every dollar works toward the same outcome: more leads and more sales.",
    bg: "https://images.unsplash.com/photo-1533750349088-cd871a92f312?q=80&w=1920&auto=format&fit=crop",
  },
  flagship: {
    eyebrow: "The flagship four",
    heading: "Four engines. One revenue system.",
    sub: "The core of every engagement \u2014 the services that decide whether you're found, chosen and remembered in the AI age.",
    items: [
      {
        icon: Search,
        title: "Foundational SEO\u2122",
        line: "The bedrock. Technical health, content and authority that lift every other channel.",
      },
      {
        icon: Sparkles,
        title: "GEO",
        line: "Get cited by ChatGPT, Perplexity and AI Overviews \u2014 visibility where answers now happen.",
      },
      {
        icon: MousePointerClick,
        title: "Advanced PPC",
        line: "High-intent demand captured today \u2014 tracked all the way to revenue.",
      },
      {
        icon: Repeat,
        title: "Retargeting",
        line: "The 97% who leave don't have to stay gone. Bring them back across every network.",
      },
    ],
  },
  catalog: {
    eyebrow: "The full catalog",
    heading: "Fifteen services. Five disciplines. Zero silos.",
    sub: "Modern buyers move from a search, to a scroll, to a screen \u2014 so every service below plugs into The Web Marketing Machine\u2122 and shares one strategy, one dataset and one report.",
    groups: [
      {
        key: "search",
        name: "Search",
        tagline: "Be the answer \u2014 everywhere answers happen.",
        items: [
          {
            icon: Search,
            title: "Foundational SEO\u2122",
            line: "The technical, content and authority bedrock that makes every channel cheaper and stronger.",
          },
          {
            icon: Sparkles,
            title: "GEO \u2014 Generative Engine Optimization",
            line: "Engineered entities, answers and authority that ChatGPT, Perplexity and AI Overviews cite.",
          },
          {
            icon: MousePointerClick,
            title: "Advanced PPC",
            line: "Surgical bidding, creative testing and revenue tracking on Google and Bing.",
          },
          {
            icon: MapPin,
            title: "Local & Map Listing Optimization",
            line: "Own the map pack in every city you serve \u2014 reviews, citations and proximity signals tuned.",
          },
          {
            icon: ShieldCheck,
            title: "Online Reputation Management",
            line: "Monitor, protect and repair what search says about your brand \u2014 before it costs you sales.",
          },
        ],
      },
      {
        key: "social",
        name: "Social",
        tagline: "Brand gravity where your buyers scroll.",
        items: [
          {
            icon: Megaphone,
            title: "Social Media Strategy",
            line: "Channel plans, calendars and creative direction that compound \u2014 not vanity metrics.",
          },
          {
            icon: Users,
            title: "Community & Follower Growth",
            line: "Audience acquisition campaigns that grow followings with real buyers \u2014 never bots.",
          },
        ],
      },
      {
        key: "content",
        name: "Content",
        tagline: "The fuel search and AI engines run on.",
        items: [
          {
            icon: Video,
            title: "Video Marketing",
            line: "Short-form and long-form video engineered for search, social and AI answer engines.",
          },
          {
            icon: FileText,
            title: "Blog Design & Development",
            line: "Editorial engines that publish the content generative engines love to cite.",
          },
          {
            icon: Link2,
            title: "Digital PR & Outreach",
            line: "Authority-building placements and outreach that earn the links AI engines trust.",
          },
        ],
      },
      {
        key: "websites",
        name: "Websites",
        tagline: "Fast, secure and built to rank from day one.",
        items: [
          {
            icon: MonitorSmartphone,
            title: "SEO-Smart WordPress Design",
            line: "Mobile-first WordPress builds that exceed search, security and Core Web Vitals standards.",
          },
          {
            icon: PenTool,
            title: "Branding & Creative",
            line: "Identity, messaging and design systems that make every campaign instantly recognizable.",
          },
        ],
      },
      {
        key: "specialty",
        name: "Specialty Media",
        tagline: "Precision channels most agencies can't run.",
        items: [
          {
            icon: Repeat,
            title: "Retargeting",
            line: "The 97% of visitors who leave, brought back across display, social and video networks.",
          },
          {
            icon: Radar,
            title: "Geofence Campaigns",
            line: "Draw a fence around competitors, events or neighborhoods \u2014 and advertise to everyone inside.",
          },
          {
            icon: Tv,
            title: "Streaming TV Advertising",
            line: "CTV/OTT campaigns that put your brand on the big screen with digital-grade targeting.",
          },
        ],
      },
    ],
  },
  machine: {
    eyebrow: "How it fits together",
    heading: "No \u00e0-la-carte chaos. One connected machine.",
    sub: "Buy one service or all fifteen \u2014 either way, your engagement runs inside The Web Marketing Machine\u2122.",
    points: [
      {
        icon: Workflow,
        text: "Every service feeds shared data \u2014 keywords, audiences, creatives and conversions.",
      },
      {
        icon: Cog,
        text: "The automation core routes budget to whatever is converting cheapest this week.",
      },
      {
        icon: BarChart3,
        text: "One US-based strategy team, one report, one number that matters: revenue.",
      },
    ],
  },
  cta: {
    heading: "Not sure where to start?",
    sub: "Get a free Visibility Audit \u2014 we'll show you exactly which services will move your number fastest, from a US-based strategist, in 2 business days or less.",
    primaryText: "Get My Free Visibility Audit",
    secondaryText: "Talk to a Strategist",
    secondaryHref: "https://www.slingshotseo.com/contact/",
  },
};

// ---------- Services FAQ (also powers FAQPage schema) ----------
export const SERVICES_FAQ = {
  heading: "Questions about working with us",
  items: [
    {
      q: "Do I need to buy everything, or can I start with one service?",
      a: "Start anywhere. Every service plugs into the same machine, so a single engagement still benefits from shared data and strategy. Most clients begin with the Free Visibility Audit and the flagship four.",
    },
    {
      q: "What's the difference between SEO and GEO?",
      a: "SEO earns rankings in traditional search results. GEO earns citations inside AI-generated answers. They share the same foundations \u2014 authority, entities, structured content \u2014 and we engineer both together.",
    },
    {
      q: "How fast will I see results?",
      a: "PPC and retargeting capture demand from day one. SEO and GEO compound over months \u2014 our StrikingDistance\u2122 methodology targets near-wins first, so meaningful movement typically shows early.",
    },
    {
      q: "How do you report performance?",
      a: "One report, revenue-first: rankings, AI citations, paid efficiency and cost per lead \u2014 reviewed with your US-based strategist. No vanity dashboards.",
    },
    {
      q: "Do you work in regulated industries?",
      a: "Yes. We run compliant playbooks for healthcare, insurance, legal and financial services \u2014 including claims-safe copy and privacy-aware tracking.",
    },
    {
      q: "What does the Free Visibility Audit include?",
      a: "Your rankings, AI citations, map presence and paid efficiency versus real competitors \u2014 plus the fastest path to more leads. Delivered in 2 business days or less.",
    },
  ],
};
